import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { SignOutIcon, CreditCardIcon, UserIcon, ShieldIcon, LightningIcon, PencilSimpleIcon, CheckIcon, XIcon, CircleNotchIcon, FloppyDiskIcon, Coins as CoinsIcon, ArrowSquareOut as ArrowSquareOutIcon } from '@phosphor-icons/react';
import { supabase } from '../lib/supabase';
import { ThemeSwitcher } from './ThemeSwitcher';

export const Settings: React.FC = () => {
    const { user, login, logout, updateProfile, creditBalance, createCheckoutSession } = useAuth();

    // Edit Mode State
    const [isEditing, setIsEditing] = useState(false);
    const [newName, setNewName] = useState('');
    const [newUsername, setNewUsername] = useState('');

    // Availability State
    const [isChecking, setIsChecking] = useState(false);
    const [isAvailable, setIsAvailable] = useState<boolean | null>(null);
    const [isSaving, setIsSaving] = useState(false);

    // Initialize form when editing starts
    useEffect(() => {
        if (isEditing && user) {
            setNewName(user.name);
            setNewUsername(user.username || '');
            setIsAvailable(true); // Assuming current is valid
        }
    }, [isEditing, user]);

    // Check availability
    useEffect(() => {
        if (!isEditing || !user) return;

        // If it's their own username, it's available
        if (newUsername === user.username) {
            setIsAvailable(true);
            return;
        }

        const checkAvailability = async () => {
            if (newUsername.length < 3) {
                setIsAvailable(null);
                return;
            }
            // Skip RPC check - DB unique constraint catches duplicates
            setIsChecking(false);
            setIsAvailable(true);
        };

        const timer = setTimeout(checkAvailability, 500);
        return () => clearTimeout(timer);
    }, [newUsername, isEditing, user]);

    const handleSaveProfile = async () => {
        if (!isAvailable || newUsername.length < 3) return;
        setIsSaving(true);
        try {
            await updateProfile({ name: newName, username: newUsername });
            setIsEditing(false);
        } catch (err) {
            console.error("Update failed", err);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="flex-1 min-h-screen bg-carbon px-6 md:px-10 py-10 w-full relative z-10 text-white">
            <div className="max-w-[800px] mx-auto">
                <div className="mb-8 border-b border-white/5 pb-6">
                    <h1 className="text-3xl font-heading font-bold mb-2">Settings</h1>
                    <p className="text-zinc-500 text-sm">Manage your account and subscription.</p>
                </div>

                {/* Appearance */}
                <div className="bg-onyx border border-white/5 rounded-xl p-6 mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                        <h3 className="text-lg font-bold">Appearance</h3>
                        <p className="text-zinc-500 text-sm">Day, Night or Midnight across the whole studio.</p>
                    </div>
                    <ThemeSwitcher className="sm:w-80" />
                </div>

                {!user ? (
                    <div className="bg-onyx border border-white/5 rounded-2xl p-8 text-center">
                        <UserIcon className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
                        <h2 className="text-xl font-bold mb-2">You are currently Anonymous</h2>
                        <p className="text-zinc-400 mb-6">Sign in to save your projects and access creative tools.</p>
                        <button
                            onClick={() => login('free')}
                            className="px-6 py-2 bg-white text-black font-bold rounded-lg hover:bg-zinc-200 transition-colors"
                        >
                            Sign In / Register
                        </button>
                    </div>
                ) : (
                    <div className="space-y-6">
                        {/* Profile Card */}
                        <div className="bg-onyx border border-white/5 rounded-xl p-6 relative overflow-hidden">
                            {!isEditing ? (
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-4">
                                        <img src={user.avatar} alt="Avatar" className="w-16 h-16 rounded-full border border-white/10" />
                                        <div>
                                            <h3 className="text-lg font-bold flex items-center gap-2">
                                                {user.name}
                                                <span className="text-zinc-500 font-normal text-sm">@{user.username || 'unknown'}</span>
                                            </h3>
                                            <p className="text-zinc-500 text-sm">{user.email}</p>
                                            <div className="flex items-center gap-2 mt-2">
                                                <span className={`text-[10px] px-2 py-0.5 rounded font-mono uppercase ${user.plan === 'pro' ? 'bg-yellow-500/10 text-yellow-500 border border-yellow-500/20' : 'bg-zinc-800 text-zinc-400'}`}>
                                                    {user.plan} Plan
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex gap-2">
                                        <button
                                            onClick={() => setIsEditing(true)}
                                            className="p-2 text-zinc-500 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                                            title="Edit Profile"
                                        >
                                            <PencilSimpleIcon className="w-4 h-4" />
                                        </button>
                                        <button
                                            onClick={logout}
                                            className="p-2 text-zinc-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                                            title="Sign Out"
                                        >
                                            <SignOutIcon className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div className="space-y-4 animate-in fade-in slide-in-from-top-2">
                                    <div className="flex justify-between items-start mb-4">
                                        <h3 className="text-lg font-bold">Edit Profile</h3>
                                        <button onClick={() => setIsEditing(false)} className="text-zinc-500 hover:text-white"><XIcon className="w-5 h-5" /></button>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="space-y-1">
                                            <label className="text-xs font-bold text-zinc-500 uppercase">Display Name</label>
                                            <input
                                                value={newName}
                                                onChange={(e) => setNewName(e.target.value)}
                                                className="w-full bg-carbon/40 border border-white/10 rounded-lg px-3 py-2 text-sm focus:border-white/30 outline-none"
                                            />
                                        </div>
                                        <div className="space-y-1">
                                            <label className="text-xs font-bold text-zinc-500 uppercase">Username</label>
                                            <div className="relative">
                                                <input
                                                    value={newUsername}
                                                    onChange={(e) => setNewUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                                                    className={`w-full bg-carbon/40 border rounded-lg px-3 py-2 text-sm outline-none font-mono ${isAvailable === true ? 'border-emerald-500/50' :
                                                        isAvailable === false ? 'border-red-500/50' : 'border-white/10'
                                                        }`}
                                                />
                                                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                                    {isChecking ? <CircleNotchIcon className="w-3 h-3 animate-spin text-zinc-500" /> :
                                                        isAvailable === true ? <CheckIcon className="w-3 h-3 text-emerald-500" /> :
                                                            isAvailable === false ? <XIcon className="w-3 h-3 text-red-500" /> : null}
                                                </div>
                                            </div>
                                            {isAvailable === false && <p className="text-[10px] text-red-400">Username taken</p>}
                                        </div>
                                    </div>

                                    <div className="flex justify-end pt-2">
                                        <button
                                            onClick={handleSaveProfile}
                                            disabled={isSaving || !isAvailable || !newName}
                                            className="px-4 py-2 bg-white text-black text-sm font-bold rounded-lg hover:bg-zinc-200 disabled:opacity-50 flex items-center gap-2"
                                        >
                                            {isSaving ? <CircleNotchIcon className="w-4 h-4 animate-spin" /> : <FloppyDiskIcon className="w-4 h-4" />}
                                            Save Changes
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Billing & Credits */}
                        <div className="bg-onyx border border-white/5 rounded-xl p-6">
                            <div className="flex items-center gap-3 mb-6">
                                <CreditCardIcon className="w-5 h-5 text-white" />
                                <h3 className="text-base font-bold">Billing & Credits</h3>
                            </div>

                            {/* Credit Balance */}
                            <div className="p-4 rounded-xl border border-white/5 bg-white/5 mb-4">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <CoinsIcon className="w-5 h-5 text-yellow-400" weight="fill" />
                                        <div>
                                            <div className="text-white font-bold text-lg">{creditBalance} credits</div>
                                            <div className="text-zinc-400 text-xs">Available balance</div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Current Plan */}
                            <div className="p-4 rounded-xl border border-white/5 mb-4">
                                <div className="flex items-center justify-between">
                                    <div>
                                        <div className="flex items-center gap-2 mb-1">
                                            {(user.subscription_tier === 'pro') && <LightningIcon className="w-4 h-4 text-yellow-400" weight="fill" />}
                                            {(user.subscription_tier === 'starter') && <LightningIcon className="w-4 h-4 text-blue-400" weight="fill" />}
                                            <span className="text-white font-medium text-sm capitalize">{user.subscription_tier || 'Free'} Plan</span>
                                            {user.subscription_status === 'active' && (
                                                <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-500/20 text-green-400 font-medium">Active</span>
                                            )}
                                        </div>
                                        <p className="text-zinc-500 text-xs">
                                            {(!user.subscription_tier || user.subscription_tier === 'free')
                                                ? 'Upgrade for monthly credits and more features'
                                                : user.subscription_tier === 'starter'
                                                ? '500 credits/month'
                                                : '1,500 credits/month'}
                                        </p>
                                    </div>
                                    {user.stripe_customer_id && (
                                        <button
                                            onClick={async () => {
                                                try {
                                                    const { data } = await supabase.functions.invoke('get-billing-portal', {
                                                        body: { userId: user.id }
                                                    });
                                                    if (data?.url) window.open(data.url, '_blank');
                                                } catch (e) {
                                                    console.error('Failed to open billing portal:', e);
                                                }
                                            }}
                                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-zinc-300 hover:text-white bg-white/5 hover:bg-white/10 rounded-lg border border-white/5 transition-colors"
                                        >
                                            <ArrowSquareOutIcon className="w-3.5 h-3.5" />
                                            Manage Subscription
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* Quick Actions */}
                            <div className="flex gap-3">
                                {(!user.subscription_tier || user.subscription_tier === 'free') && (
                                    <button
                                        onClick={() => {
                                            createCheckoutSession('price_1Sz2GRJtyB9kWazghXzuggab', 'subscription');
                                        }}
                                        className="flex-1 py-2.5 bg-gradient-to-r from-blue-500 to-indigo-600 text-white text-xs font-bold rounded-lg hover:from-blue-400 hover:to-indigo-500 transition-all"
                                    >
                                        Upgrade Plan
                                    </button>
                                )}
                                <button
                                    onClick={() => createCheckoutSession('price_1SyrkoJtyB9kWazgSIvuQ6yG', 'payment')}
                                    className="flex-1 py-2.5 bg-white/10 text-white text-xs font-bold rounded-lg hover:bg-white/20 border border-white/5 transition-colors"
                                >
                                    Buy 500 Credits — $9.99
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};